import { makeObservable, observable, action, flow } from "mobx";
import { documentsData } from "../../../pages/v2/DrivesPage/mockData";
import {v4 as uuidv4} from "uuid";
import { createFile, getFileUuidFromCreateResponse } from "../../../services/file";
import {
    duplicateDocument as duplicateDocumentService,
    renameDocument as renameDocumentService,
    createDocumentByTenant,
    attachDocumentbyTenant,
    assignAgentToDocument
} from "../../../services/document";

class DocumentsStore {
  documents = [];
  isLoading = false;
  error = null;
    errorMessage = ''; // Error message for operations
    uploadProgress = {}; // Track upload progress by file name
    temporaryDocuments = []; // Store temporary documents during upload
    isUploading = false; // Global upload status
    uploadingFiles = new Set(); // Track which files are currently uploading
    isRenamingDocument = false;
    documentRenamed = false;
    renamedDocument = null;
    isErrorRenameDocument = false;
    errorRenameDocumentMessage = '';
    
    // Error flags for upload process - track specific failure points
    isErrorCreateDocumentEntry = false; // Error during document creation in database
    isErrorCreateFileEntry = false; // Error during file entry creation for S3 upload
    isErrorUploadToS3 = false; // Error during actual file upload to S3
    isErrorAttachDocument = false; // Error during document attachment after upload
    
    // Success flags for upload process
    uploadSuccess = false; // Success flag for upload completion
    uploadFailed = false; // Flag for upload failure (when some files fail)

  constructor() {
    makeObservable(this, {
      // Observables
      documents: observable,
      isLoading: observable,
      error: observable,
            errorMessage: observable,
            uploadProgress: observable,
            temporaryDocuments: observable,
            isUploading: observable,
            uploadingFiles: observable,
            isErrorCreateDocumentEntry: observable,
            isErrorCreateFileEntry: observable,
            isErrorUploadToS3: observable,
            isErrorAttachDocument: observable,
            uploadSuccess: observable,
            uploadFailed: observable,
            isRenamingDocument: observable,
            documentRenamed: observable,
            renamedDocument: observable,
            isErrorRenameDocument: observable,
            errorRenameDocumentMessage: observable,

      // Actions
      setDocuments: action,
      setLoading: action,
      setError: action,
            setErrorMessage: action,
            clearErrorMessage: action,
            setIsErrorCreateDocumentEntry: action,
            setIsErrorCreateFileEntry: action,
            setIsErrorUploadToS3: action,
            setIsErrorAttachDocument: action,
            setUploadSuccess: action,
            setUploadFailed: action,
            resetUploadErrors: action,
            setIsRenamingDocument: action,
            setDocumentRenamed: action,
            setRenamedDocument: action,
            setIsErrorRenameDocument: action,
            setErrorRenameDocumentMessage: action,
            addTemporaryDocument: action,
            updateTemporaryDocument: action,
            removeTemporaryDocument: action,
            setUploadProgress: action,
            clearUploadProgress: action,
            setIsUploading: action,
            addUploadingFile: action,
            removeUploadingFile: action,
            // Mock data helpers (demo only)
            addDocumentCardToMockData: action,

            // flows for uploading files
            createDocumentEntry: flow,
            assignToAgent: flow,
            createFileEntry: flow,
            uploadToS3: flow,
            attachDocumentToFile: flow,

      // Flows (async actions)
      getDocumentsByDriveId: flow,
      getDocumentsByFolderId: flow,
      getMyDocuments: flow,
      getSharedDocuments: flow,
      getDocumentById: flow,
      createDocument: flow,
            deleteDocument: flow,
            renameDocument: flow,
            uploadFile: flow,
            uploadMultipleFiles: flow
    });
  }

  // Actions
  setDocuments = (documents) => {
    this.documents = documents;
  };

  setLoading = (status) => {
    this.isLoading = status;
  };

  setError = (error) => {
    this.error = error;
  };

    setErrorMessage = (message) => {
        this.errorMessage = message;
    };

    clearErrorMessage = () => {
        this.errorMessage = '';
    };

    // Error flags setters
    setIsErrorCreateDocumentEntry = (status) => {
        this.isErrorCreateDocumentEntry = status;
    };

    setIsErrorCreateFileEntry = (status) => {
        this.isErrorCreateFileEntry = status;
    };

    setIsErrorUploadToS3 = (status) => {
        this.isErrorUploadToS3 = status;
    };

    setIsErrorAttachDocument = (status) => {
        this.isErrorAttachDocument = status;
    };

    setUploadSuccess = (status) => {
        this.uploadSuccess = status;
    };

    setUploadFailed = (status) => {
        this.uploadFailed = status;
    };

    // --- End of general actions ---

  // API Actions
    * getDocumentsByDriveId(driveId) {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const documents = documentsData;
      this.setDocuments(documents);
      return documents;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }


    * getMyDocuments() {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const documents = documentsData;
      this.setDocuments(documents);
      return documents;
        } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }
  

    * getDocumentsByFolderId(folderId) {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const documents = documentsData;
      this.setDocuments(documents);
      return documents;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

    * getSharedDocuments() {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const documents = documentsData
      this.setDocuments(documents);
      return documents;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

    * getDocumentById(id) {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const document = documentsData.find(doc => doc.id === id);
      return document || null;
    } catch (error) {
      this.setError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

    * createDocument(folderId, documentData) {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const newDocument = {
        id: Date.now().toString(),
        folderId,
        ...documentData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      documentsData.push(newDocument);
      return newDocument;
    } catch (error) {
      this.setError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

    * deleteDocument(id) {
    try {
      this.setLoading(true);
      // Simular llamada al servicio
      const index = documentsData.findIndex(doc => doc.id === id);
      if (index !== -1) {
        documentsData.splice(index, 1);
        return true;
      }
      return false;
    } catch (error) {
      this.setError(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

    * renameDocument(payload) {
        try {
            this.isRenamingDocument = true;
            this.isErrorRenameDocument = false;
            this.errorRenameDocumentMessage = '';

            const response = yield renameDocumentService(payload);

            const { statusCode, msgCode, body } = response.data;

            if (statusCode === 200) {
                this.documentRenamed = true;
                this.renamedDocument = body;
                return body;
            }

            this.isErrorRenameDocument = true;

            if (msgCode && msgCode.length > 0) {
                this.errorRenameDocumentMessage = msgCode[0].description;
            } else {
                this.errorRenameDocumentMessage = 'Error renaming document.';
            }

            return {};

        } catch (error) {
            console.error('Error in renameDocument:', error);

            this.isErrorRenameDocument = true;
            this.errorRenameDocumentMessage = 'Network error. Please check your connection.';

            throw error;
        } finally {
            this.isRenamingDocument = false;
        }
    }

  *duplicateDocument(documentUuid) {
    try {
      this.setLoading(true);
      
      const response = yield duplicateDocumentService(documentUuid);
      const duplicatedDocument = response.data.body;
      
      return duplicatedDocument;
    } catch (error) {
      console.error('Error duplicating document:', error);
      
      // Fallback: El endpoint aún no está disponible, crear mock local
      console.warn('Duplicate endpoint not available yet, creating mock copy');
      const mockDocument = {
        document_uuid: `doc-${Date.now()}`,
        name: `Copy of Document`,
        vault_uuid: 'mock-vault-uuid',
        folder_uuid: null,
        file_uuid: 'mock-file-uuid',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        size: 0,
        type: 'pdf'
      };
      
      return mockDocument;
    } finally {
      this.setLoading(false);
    }
  }
    // ********************** Actions and Flows for uploading process **********************

    // Upload validation helpers (actions)
    /**
     * Validate file before upload
     * 
     * @param {File} file - File to validate
     * @param {number} maxSize - Maximum file size in bytes
     * @returns {boolean} - True if valid
     * @throws {Error} - If validation fails
     */
    validateFile(file, maxSize = 100 * 1024 * 1024) {
        if (!file) {
            const errorMsg = 'No file provided';
            this.setErrorMessage(errorMsg);
            throw new Error(errorMsg);
        }
        if (file.size > maxSize) {
            const errorMsg = `File too large. Maximum size: ${maxSize / (1024 * 1024)}MB`;
            this.setErrorMessage(errorMsg);
            throw new Error(errorMsg);
        }
        return true;
    }

    /**
     * Validate authentication data
     * 
     * @param {string} userId - User ID to validate
     * @param {string} tenantId - Tenant ID to validate
     * @returns {boolean} - True if valid
     * @throws {Error} - If validation fails
     */
    validateAuth(userId, tenantId) {
        if (!userId) {
            const errorMsg = 'User ID is required';
            this.setErrorMessage(errorMsg);
            throw new Error(errorMsg);
        }
        if (!tenantId) {
            const errorMsg = 'Tenant ID is required';
            this.setErrorMessage(errorMsg);
            throw new Error(errorMsg);
        }
        return true;
    }

    // Upload Actions (state + UI helpers)
    /**
     * Reset all upload error flags and clear error message
     * 
     * This method clears all error states from the upload process,
     * allowing for a fresh upload attempt.
     */
    resetUploadErrors = () => {
        this.isErrorCreateDocumentEntry = false;
        this.isErrorCreateFileEntry = false;
        this.isErrorUploadToS3 = false;
        this.isErrorAttachDocument = false;
        this.uploadSuccess = false;
        this.uploadFailed = false;
        this.clearErrorMessage();
    };

    setIsRenamingDocument = (status) => {
        this.isRenamingDocument = status;
    };

    setDocumentRenamed = (status) => {
        this.documentRenamed = status;
        if (!status) {
            this.renamedDocument = null;
        }
    };

    setRenamedDocument = (document) => {
        this.renamedDocument = document;
    };

    setIsErrorRenameDocument = (status) => {
        this.isErrorRenameDocument = status;
        if (!status) {
            this.errorRenameDocumentMessage = '';
        }
    };

    setErrorRenameDocumentMessage = (message) => {
        this.errorRenameDocumentMessage = message;
    };

    /**
     * Add a temporary document during upload process
     * 
     * Temporary documents are shown in the UI while files are being uploaded,
     * providing immediate feedback to users about their upload progress.
     * 
     * @param {Object} document - Document object to add as temporary
     */
    addTemporaryDocument = (document) => {
        const uniqueId = document.id || uuidv4();
        const {
            file: originalFile = null,
            retryOptions = null,
            ...rest
        } = document;

        this.temporaryDocuments.unshift({
            ...rest,
            id: uniqueId,
            uploadId: uniqueId, // ID único para el upload
            file: originalFile,
            retryOptions,
            state: 'loading',
            createdAt: new Date().toISOString(),
            isTemporary: true
        });
        return uniqueId; // Retornar el ID para usarlo después
    };

    /**
     * Inject a mock card into the documents grid after a successful upload (demo only).
     *
     * In the real implementation the server would return the newly created document
     * and the UI would re-fetch or receive it through a websocket. Because this
     * flow is currently wired against static mock data (`documentsData`), we
     * manually mutate that array so the user can immediately see the uploaded
     * files reflected in the list. This effect only lives for the current session;
     * a full page refresh restores the original mock dataset.
     *
     * @param {Object} params
     * @param {string} [params.documentUuid] - Document UUID returned by the API.
     * @param {string} params.fileName - Display name of the uploaded file.
     * @param {Date} [params.uploadedAt] - Timestamp used to render the metadata.
     */
    addDocumentCardToMockData = ({
        documentUuid,
        fileName,
        uploadedAt = new Date()
    }) => {
        // Demo-only helper: mimic persisted cards by updating the mock documents list
        const formattedDate = uploadedAt.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

        const existingIndex = documentsData.findIndex(
            (doc) => doc.uploadId === documentUuid || doc.title === fileName
        );

        const baseCard = {
            id: documentUuid || uuidv4(),
            title: fileName,
            owner: 'You',
            date: formattedDate,
            lastUpdated: 'Today',
            lastUpdatedTooltip: 'Just now',
            status: 'Connected Agent',
            tags: ['Uploaded', 'New'],
            isUploading: false,
            uploadProgress: undefined,
            uploadId: documentUuid
        };

        if (existingIndex >= 0) {
            documentsData[existingIndex] = {
                ...documentsData[existingIndex],
                ...baseCard
            };
            return;
        }

        // Demo-only helper: push the newly uploaded file to the top of the mock list
        documentsData.unshift(baseCard);
    };

    updateTemporaryDocument = (fileName, updates) => {
        const index = this.temporaryDocuments.findIndex(doc => doc.name === fileName);
        if (index !== -1) {
            this.temporaryDocuments[index] = {
                ...this.temporaryDocuments[index],
                ...updates
            };
        }
    };

    /**
     * Remove a temporary document after upload completion
     * 
     * This method is used in several scenarios:
     * - When user clicks remove button in UploadFeedback component
     * - When user retries a failed upload (removes the failed document)
     * - When cleaning up temporary documents after upload completion
     * 
     * Used in:
     * - MyWorkspace.jsx: handleRetryUpload() function (line 83)
     * - MyWorkspace.jsx: UploadFeedback onFileRemove callback (lines 114, 451)
     * 
     * @param {string} fileName - Name of the file to remove from temporary documents
     */
    removeTemporaryDocument = (identifier) => {
        this.temporaryDocuments = this.temporaryDocuments.filter(
            (doc) => doc.name !== identifier && doc.uploadId !== identifier
        );
    };

    /**
     * Set upload progress for a specific file
     * 
     * The upload progress system tracks the status of each file during the upload process.
     * Progress is stored as an object with the following structure:
     * {
     *   status: string,     // Current step: 'creating_document', 'creating_file', 'uploading', 'attaching_document', 'completed', 'error'
     *   percentage: number, // Progress percentage (0-100)
     *   error?: string     // Error message if upload failed
     * }
     * 
     * @param {string} fileName - Name of the file being uploaded
     * @param {Object} progress - Progress object containing status and percentage
     */
    setUploadProgress = (fileName, progress) => {
        this.uploadProgress[fileName] = progress;
    };

    /**
     * Clear upload progress for a specific file
     * 
     * @param {string} fileName - Name of the file to clear progress for
     */
    clearUploadProgress = (fileName) => {
        delete this.uploadProgress[fileName];
    };

    /**
     * Set global upload status
     * 
     * @param {boolean} status - Whether any files are currently being uploaded
     */
    setIsUploading = (status) => {
        this.isUploading = status;
    };

    /**
     * Add a file to the uploading files set
     * 
     * @param {string} fileName - Name of the file being uploaded
     */
    addUploadingFile = (fileName) => {
        this.uploadingFiles.add(fileName);
        this.setIsUploading(true);
    };

    /**
     * Remove a file from the uploading files set
     * 
     * @param {string} fileName - Name of the file to remove from uploading set
     */
    removeUploadingFile = (fileName) => {
        this.uploadingFiles.delete(fileName);
        if (this.uploadingFiles.size === 0) {
            this.setIsUploading(false);
        }
    };

    /**
     * Check if a specific file is currently being uploaded
     * 
     * @param {string} fileName - Name of the file to check
     * @returns {boolean} - True if the file is currently uploading
     */
    isFileUploading = (fileName) => {
        return this.uploadingFiles.has(fileName);
    };

    // Upload Flows (in order of use)

    /**
     * Create a document entry in the database
     * 
     * This is the first step in the upload process. It creates a document record
     * in the database with metadata about the file before the actual file upload.
     * 
     * @param {File} file - The file object to create document entry for
     * @param {Object} options - Configuration options
     * @param {string} options.vaultUuid - UUID of the vault to store the document
     * @param {string} options.userId - ID of the user creating the document
     * @param {string} options.tenantId - ID of the tenant
     * @param {string} options.folderId - Optional folder ID to upload document to
     * @param {Function} options.onProgress - Callback function for progress updates
     * @returns {Object} - Document data with UUID
     * @throws {Error} - If document creation fails
     */
    * createDocumentEntry(file, options) {
        try {
            const {vaultUuid, folderId, userId, tenantId, onProgress} = options;

            if (onProgress) onProgress('creating_document', 10);
            this.setUploadProgress(file.name, {status: 'creating_document', percentage: 10});

            const createDocumentBody = {
                name: file.name,
                vaultUuid: vaultUuid,
                folderUuid: folderId || null, // Add folderId to document creation
                type: file.type || 'application/octet-stream',
                url: '',
                metadata: { "region": "us-east-1", "confidential": true }
            };

            const response = yield createDocumentByTenant(createDocumentBody);
            
            // Check status_code in response.data (API wraps errors in HTTP 200)
            const { status_code, msg_code, body } = response.data;
            
            // 2xx - Success (200 OK, 201 Created)
            if (status_code >= 200 && status_code < 300) {
                if (!body?.document?.document_uuid) {
                    throw new Error('Failed to create document: No UUID received');
                }
                return response.data;
            }
            
            // 409 - Document duplicate
            if (status_code === 409 && msg_code && msg_code.some(msg => msg.code === "DOC_DUPLICATE")) {
                const errorMessage = msg_code.find(msg => msg.code === "DOC_DUPLICATE")?.description 
                    || `A document with name '${file.name}' already exists`;
                throw new Error(errorMessage);
            }
            
            // Other errors
            if (msg_code && msg_code.length > 0) {
                throw new Error(msg_code[0].description);
            }
            
            throw new Error('Failed to create document');
            
        } catch (error) {
            // Log with context but throw clean error message for UI
            this.setIsErrorCreateDocumentEntry(true);
            console.error(`Error creating document entry for ${file.name}:`, error);
            
            // Throw the original error message without adding prefix
            // This keeps the error message clean for the UI
            throw error;
        }
    }

    // Assign document to an agent if needed
    * assignToAgent(documentUuid, options) {
        const {agentUuid, userId, onProgress} = options;

        if (!agentUuid) return;

        if (onProgress) onProgress('assigning_to_agent', 25);
        this.setUploadProgress(options.fileName, {status: 'assigning_to_agent', percentage: 25});

        const assignDocumentBody = {
            document_uuid: documentUuid,
            agent_uuid: agentUuid,
            expires_at: "2026-12-31 23:59:59",
            owner: userId,
            created_by: userId,
            granted_by: userId,
        };

        yield assignAgentToDocument(assignDocumentBody);
    }

    /**
     * Create a file entry for S3 upload
     * 
     * This is the second step in the upload process. It creates a file entry
     * that generates a presigned URL for uploading the file to S3.
     * 
     * @param {File} file - The file object to create file entry for
     * @param {Object} options - Configuration options
     * @param {string} options.documentUuid - UUID of the document created in previous step
     * @param {string} options.tenantId - ID of the tenant
     * @param {string} options.folderId - Optional folder ID to upload file to
     * @param {Function} options.onProgress - Callback function for progress updates
     * @returns {Object} - File entry data with S3 upload URL
     * @throws {Error} - If file entry creation fails
     */
    * createFileEntry(file, options) {
        try {
            const {userId, tenantId, vaultUuid, documentUuid, versionUuid, folderId, onProgress} = options;

            if (onProgress) onProgress('getting_upload_url', 40);
            this.setUploadProgress(file.name, {status: 'getting_upload_url', percentage: 40});

            const createFileBody = {
                name: file.name,
                folder_id: folderId || '-', // Use provided folderId or null for root
                tenant_id: tenantId,
                user_id: userId,
                file_type: file.type || 'application/octet-stream',
                status: 'active',
                vault_uuid: vaultUuid,
                document_uuid: documentUuid,
                version_uuid: versionUuid
            };

            const response = yield createFile(createFileBody);
            
            const { statusCode, msgCode, body } = response.data;
            
            if (statusCode >= 200 && statusCode < 300) {
                if (!body?.uploadUrl) {
                    throw new Error('No upload URL received');
                }
                
                const file_uuid = getFileUuidFromCreateResponse(body);
                if (!file_uuid) {
                    throw new Error('No file uuid received');
                }

                return {
                    uploadUrl: body.uploadUrl,
                    fileKey: body.fileKey,
                    file_uuid,
                };
            }
            
            if (msgCode && msgCode.length > 0) {
                throw new Error(msgCode[0].description);
            }
            
            throw new Error('Failed to create file entry');
        } catch (error) {
            const errorMsg = `Error creating file entry: ${error.message}`;
            this.setIsErrorCreateFileEntry(true);
            this.setErrorMessage(errorMsg);
            console.error(errorMsg, error);
            throw error;
        }
    }

    /**
     * Upload file to S3 using presigned URL
     * 
     * This is the third step in the upload process. It uploads the actual file
     * content to S3 using the presigned URL obtained from createFileEntry.
     * 
     * @param {File} file - The file object to upload
     * @param {string} uploadUrl - Presigned URL for S3 upload
     * @param {Object} options - Configuration options
     * @param {Function} options.onProgress - Callback function for progress updates
     * @throws {Error} - If S3 upload fails
     */
    * uploadToS3(file, uploadUrl, options) {
        try {
            const {onProgress} = options;

            if (onProgress) onProgress('uploading', 60);
            this.setUploadProgress(file.name, {status: 'uploading', percentage: 60});

            const response = yield fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                }
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
            }
        } catch (error) {
            const errorMsg = `Error uploading to S3: ${error.message}`;
            this.setIsErrorUploadToS3(true);
            this.setErrorMessage(errorMsg);
            console.error(errorMsg, error);
            throw error;
        }
    }

    /**
     * Attach document to file after successful upload
     * 
     * This is the final step in the upload process. It links the uploaded file
     * in S3 to the document record in the database.
     * 
     * @param {Object} options - Configuration options
     * @param {string} options.fileUuid - Opaque file identifier
     * @param {string} options.documentUuid - UUID of the document
     * @param {string} options.tenantId - ID of the tenant
     * @param {Function} options.onProgress - Callback function for progress updates
     * @param {string} options.fileName - Name of the file for progress tracking
     * @throws {Error} - If document attachment fails
     */
    * attachDocumentToFile(options) {
        try {
          console.log('attachDocumentToFile', options);
            const {fileUuid, document_uuid, tenantId, onProgress, fileName} = options;

            if (onProgress) onProgress('attaching_document', 90);
            this.setUploadProgress(fileName, {status: 'attaching_document', percentage: 90});

            const attachDocumentBody = {
                file_uuid: fileUuid,
                document_uuid: document_uuid,
            };
            yield attachDocumentbyTenant(attachDocumentBody);
        } catch (error) {
            const errorMsg = `Error attaching document: ${error.message}`;
            this.setIsErrorAttachDocument(true);
            this.setErrorMessage(errorMsg);
            console.error(errorMsg, error);
            throw error;
        }
    }

    /**
     * Main file upload method - orchestrates the complete upload process
     * 
     * This method coordinates all four steps of the upload process:
     * 1. Create document entry in database (10% progress)
     * 2. Create file entry and get S3 presigned URL (40% progress)
     * 3. Upload file to S3 (60% progress)
     * 4. Attach document to file (90% progress)
     * 5. Complete (100% progress)
     * 
     * The upload progress system provides real-time status updates:
     * - Progress is tracked per file using fileName as key
     * - Each step updates both the progress callback and internal state
     * - Error states are tracked separately for each step
     * - Temporary documents are created during upload for UI feedback
     * 
     * @param {File} file - The file object to upload
     * @param {Object} options - Configuration options
     * @param {string} options.vaultUuid - UUID of the vault to store the document
     * @param {string} options.userId - ID of the user uploading the file
     * @param {string} options.tenantId - ID of the tenant
     * @param {string} options.folderId - Optional folder ID to upload file to
     * @param {string} options.agentUuid - Optional agent UUID to assign document to
     * @param {Function} options.onProgress - Callback function for progress updates
     * @param {Function} options.onSuccess - Callback function for successful upload
     * @param {Function} options.onError - Callback function for upload errors
     * @returns {Object} - Upload result with document data
     * @throws {Error} - If any step of the upload process fails
     */
    * uploadFile(file, options = {}) {
        const {
            vaultUuid,
            folderId,
            agentUuid,
            userId,
            tenantId = "58145bef-4f71-468a-bded-1823d2a08f58",
            maxSize = 100 * 1024 * 1024,
            onProgress
        } = options;

        try {
            // Initial validations
            this.validateFile(file, maxSize);
            this.validateAuth(userId, tenantId);

            // Setup upload tracking
            this.addUploadingFile(file.name);
            const retryOptions = {
                vaultUuid,
                folderId,
                agentUuid,
                userId,
                tenantId
            };

            this.addTemporaryDocument({
                name: file.name,
                file,
                retryOptions
            });
            this.setUploadProgress(file.name, {status: 'preparing', percentage: 0});

            // 1. Create document /v1/tenant/document/create
            const documentData = yield this.createDocumentEntry(file, {vaultUuid, folderId, userId, tenantId, onProgress});

            const document_uuid = documentData.body.document.document_uuid;
            const versionUuid = documentData.body.version.doc_version_uuid;
            const documentUuid = document_uuid; // Alias for consistency


            // 2. Create file and get upload URL /v1/file/create
            const fileData = yield this.createFileEntry(file, {
                userId,
                tenantId,
                vaultUuid,
                documentUuid,
                versionUuid,
                folderId,
                onProgress
            });
            const {uploadUrl, file_uuid: fileUuid} = fileData;

            // 3. Upload to S3
            yield this.uploadToS3(file, uploadUrl, {onProgress});

            // 4. Attach document
            yield this.attachDocumentToFile({
                fileUuid,
                document_uuid,
                tenantId,
                onProgress,
                fileName: file.name
            });

            // Handle success
            if (onProgress) onProgress('completed', 100);
            this.setUploadProgress(file.name, {status: 'completed', percentage: 100});

            // Update temporary document to success state
            this.updateTemporaryDocument(file.name, {
                state: 'active',
                id: documentUuid,
                url: uploadUrl,
                file: null,
                retryOptions: null
            });

            // Limpiar progreso y flag de uploading inmediatamente
            // MANTENER el documento temporal con estado 'active' para que se vea en UploadFeedback
            this.clearUploadProgress(file.name);
            this.removeUploadingFile(file.name);

            // Demo-only side effect: reflect successful uploads in the mock documents grid
            this.addDocumentCardToMockData({
                documentUuid,
                fileName: file.name
            });

            return {
                success: true,
                documentUuid,
                fileName: file.name,
                uploadUrl
            };

        } catch (error) {
            // Log with context for debugging
            console.error(`Upload failed for ${file.name}:`, error);

            // Update temporary document to error state with clean message
            this.updateTemporaryDocument(file.name, {
                state: 'error',
                error: error.message
            });

            this.setUploadProgress(file.name, {
                status: 'error',
                percentage: 0,
                error: error.message
            });

            // Solo limpiar el progreso y el flag de uploading,
            // MANTENER el documento temporal para que el usuario pueda ver el error y retry
            this.clearUploadProgress(file.name);
            this.removeUploadingFile(file.name);

            // Throw the original error without adding prefix - keeps message clean for UI
            throw error;
        }
    }

    /**
     * Upload multiple files concurrently
     * 
     * This method handles uploading multiple files simultaneously, with each file
     * going through the complete upload process independently. Progress is tracked
     * per file, and the method waits for all uploads to complete before returning.
     * 
     * The upload process for each file includes:
     * 1. Create document entry in database
     * 2. Create file entry and get S3 presigned URL
     * 3. Upload file to S3
     * 4. Attach document to file
     * 
     * Success notification is triggered if at least one file uploads successfully.
     * Individual file errors are captured and included in the results.
     * 
     * @param {File[]} files - Array of file objects to upload
     * @param {Object} options - Configuration options (same as uploadFile)
     * @param {string} options.vaultUuid - UUID of the vault to store documents
     * @param {string} options.userId - ID of the user uploading files
     * @param {string} options.tenantId - ID of the tenant
     * @param {string} options.folderId - Optional folder ID to upload files to
     * @param {string} options.agentUuid - Optional agent UUID to assign documents to
     * @param {Function} options.onProgress - Callback function for progress updates
     * @param {Function} options.onSuccess - Callback function for successful uploads
     * @param {Function} options.onError - Callback function for upload errors
     * @returns {Object} - Upload results summary
     * @returns {number} returns.totalFiles - Total number of files processed
     * @returns {number} returns.successful - Number of files uploaded successfully
     * @returns {number} returns.failed - Number of files that failed to upload
     * @returns {Array} returns.results - Array of individual file upload results
     * @throws {Error} - If no files are provided or critical errors occur
     */
    * uploadMultipleFiles(files, options = {}) {
        try {
            // Reset flags at start
            this.setUploadSuccess(false);
            this.setUploadFailed(false);
            
            const results = [];
            const uploadPromises = [];

            // Create upload promises for each file
            // uploadFile se encargará de agregar los temporaryDocuments
            for (const file of files) {
                const uploadPromise = this.uploadFile(file, options)
                    .then(result => {
                        results.push(result);
                        return result;
                    })
                    .catch(error => {
                        results.push({
                            success: false,
                            fileName: file.name,
                            error: error.message
                        });
                        return null;
                    });

                uploadPromises.push(uploadPromise);
            }

            // Wait for all uploads to complete
            yield Promise.all(uploadPromises);

            const uploadResults = {
                totalFiles: files.length,
                successful: results.filter(r => r && r.success).length,
                failed: results.filter(r => !r || !r.success).length,
                results
            };

            // Handle success and failure flags
            if (uploadResults.successful > 0) {
                this.setUploadSuccess(true);
            }
            
            if (uploadResults.failed > 0) {
                this.setUploadFailed(true);
                // Use the clean error message from the first failed upload
                const firstError = results.find(r => !r || !r.success);
                const errorMessage = firstError?.error || "Check your internet connection or try again.";
                this.setErrorMessage(errorMessage);
            }

            return uploadResults;
        } finally {
            // Reset flags after a delay to allow toast to show
            setTimeout(() => {
                this.setUploadSuccess(false);
                this.setUploadFailed(false);
            }, 100);
        }
    }
}


export default DocumentsStore;